import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Eye, EyeOff, Plus, Wand2, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { Modal, IconBtn, AddBtn, inputCls, cedis } from "./ui";

export default function BoxesAdmin({ fx }) {
  const [boxes, setBoxes] = useState([]);
  const [form, setForm] = useState(null);
  const load = async () => { const { data } = await api.get("/admin/boxes"); setBoxes(data); };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this box?")) return; await api.delete(`/admin/boxes/${id}`); toast.success("Box deleted"); load(); };
  const body = (b, patch) => ({ name: b.name, box_type: b.box_type, image: b.image, price_gbp: b.price_gbp, tagline: b.tagline || "", description: b.description || "", items: b.items || [], gallery: b.gallery || [], active: b.active, ...patch });
  const toggle = async (b) => { await api.put(`/admin/boxes/${b.id}`, body(b, { active: !b.active })); load(); };

  return (
    <div data-testid="admin-boxes">
      <div className="flex justify-end mb-4"><AddBtn label="Add box" onClick={() => setForm({})} testid="add-box-btn" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {boxes.length === 0 && <p className="text-[#5C5C5C]">No boxes yet.</p>}
        {boxes.map((b) => (
          <div key={b.id} className={`bg-white border border-black/5 rounded-2xl overflow-hidden flex flex-col ${b.active ? "" : "opacity-55"}`} data-testid={`box-row-${b.id}`}>
            <div className="relative aspect-[16/9] bg-[#E7E1B1]">
              <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
              <span className={`absolute top-2 left-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${b.box_type === "subscription" ? "bg-[#306D29] text-white" : "bg-white/90 text-[#0D530E]"}`}>{b.box_type === "subscription" ? "Monthly" : "One-time"}</span>
              {!b.active && <span className="absolute top-2 right-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#B23B3B] text-white">Hidden</span>}
            </div>
            <div className="p-4 flex flex-col flex-1">
              <p className="font-display text-lg text-[#0D530E]">{b.name}</p>
              <p className="text-xs text-[#5C5C5C] mb-2">{b.items?.length || 0} items · {b.tagline}</p>
              <p className="font-medium text-[#306D29] text-sm mb-3">{cedis(b.price_gbp, fx)} <span className="text-[#5C5C5C] font-normal">· £{b.price_gbp}</span></p>
              <div className="mt-auto flex items-center gap-0.5 pt-2 border-t border-black/5">
                <IconBtn title={b.active ? "Hide" : "Show"} testid={`box-active-${b.id}`} onClick={() => toggle(b)}>{b.active ? <Eye size={15} /> : <EyeOff size={15} />}</IconBtn>
                <IconBtn title="Edit" testid={`box-edit-${b.id}`} onClick={() => setForm(b)}><Pencil size={15} /></IconBtn>
                <IconBtn title="Delete" danger testid={`box-del-${b.id}`} onClick={() => del(b.id)}><Trash2 size={15} /></IconBtn>
              </div>
            </div>
          </div>
        ))}
      </div>
      {form && <BoxForm box={form} fx={fx} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}

function BoxForm({ box, fx, onClose, onSaved }) {
  const editing = !!box.id;
  const [f, setF] = useState({
    name: box.name || "", box_type: box.box_type || "one_time", image: box.image || "", price_gbp: box.price_gbp ?? "",
    tagline: box.tagline || "", description: box.description || "", items: box.items?.length ? box.items.map((i) => ({ ...i })) : [{ name: "", qty: 1 }], gallery: box.gallery || [], active: box.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [genning, setGenning] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [building, setBuilding] = useState(false);
  const buildAI = async () => {
    if (!aiPrompt.trim()) { toast.error("Describe the box, e.g. '2 Milo, 3 Indomie, 1 tin milk'"); return; }
    setBuilding(true);
    try {
      const { data } = await api.post("/admin/boxes/ai-build", { prompt: aiPrompt });
      setF((prev) => ({ ...prev, name: prev.name || data.name, tagline: prev.tagline || data.tagline,
        description: prev.description || data.description, items: data.items?.length ? data.items : prev.items,
        price_gbp: prev.price_gbp || data.price_gbp }));
      toast.success("Box drafted — review & save");
    } catch (e) { toast.error(e.response?.data?.detail || "AI could not build the box"); }
    finally { setBuilding(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setItem = (i, k, v) => setF({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) });
  const addItem = () => setF({ ...f, items: [...f.items, { name: "", qty: 1 }] });
  const rmItem = (i) => setF({ ...f, items: f.items.filter((_, idx) => idx !== i) });
  const payload = () => {
    const items = f.items.filter((i) => i.name.trim()).map((i) => ({ name: i.name.trim(), qty: parseInt(i.qty) || 1 }));
    return { name: f.name, box_type: f.box_type, image: f.image, price_gbp: parseFloat(f.price_gbp), tagline: f.tagline, description: f.description, items, gallery: f.gallery, active: f.active };
  };
  const genImages = async () => {
    if (!f.items.some((i) => i.name.trim())) { toast.error("Add at least one item first — box images are generated from its products."); return; }
    setGenning(true);
    try {
      let boxId = box.id;
      if (!boxId) { const { data } = await api.post("/admin/boxes", payload()); boxId = data.id; box.id = data.id; }
      else { await api.put(`/admin/boxes/${boxId}`, payload()); }
      const { data } = await api.post(`/admin/boxes/${boxId}/generate-images`);
      setF((prev) => ({ ...prev, gallery: data.gallery || [], image: data.image || prev.image }));
      toast.success("Generated 3 box images");
    } catch (e) { toast.error("Image generation failed"); }
    finally { setGenning(false); }
  };
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (box.id) await api.put(`/admin/boxes/${box.id}`, payload()); else await api.post("/admin/boxes", payload());
      toast.success(editing ? "Box updated" : "Box created"); onSaved();
    } catch { toast.error("Failed to save box"); setSaving(false); }
  };
  return (
    <Modal title={editing ? "Edit box" : "Add box"} onClose={onClose} testid="box-form">
      <form onSubmit={submit} className="space-y-3">
        <div className="rounded-xl bg-[#E7E1B1]/50 p-3 space-y-2">
          <p className="text-xs uppercase tracking-wider text-[#0D530E] font-semibold">Build with AI</p>
          <textarea className={inputCls} rows={2} placeholder="List items & quantities, e.g. '2 Milo, 3 Indomie noodles, 1 tin milk powder'" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid="bf-ai-prompt" />
          <button type="button" onClick={buildAI} disabled={building} data-testid="bf-ai-build"
            className="flex items-center gap-1.5 text-sm bg-[#306D29] hover:bg-[#0D530E] text-white px-3 py-1.5 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
            {building ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} {building ? "Building…" : "Draft box from prompt"}
          </button>
        </div>
        <input required className={inputCls} placeholder="Box name" value={f.name} onChange={set("name")} data-testid="bf-name" />
        <div className="grid grid-cols-2 gap-3">
          <select className={inputCls} value={f.box_type} onChange={set("box_type")} data-testid="bf-type">
            <option value="one_time">One-time box</option>
            <option value="subscription">Subscription (monthly)</option>
          </select>
          <input required type="number" step="0.01" className={inputCls} placeholder="Price £" value={f.price_gbp} onChange={set("price_gbp")} data-testid="bf-price" />
        </div>
        <p className="text-sm text-[#306D29] font-medium -mt-1">Customer pays: {cedis(f.price_gbp, fx)}{f.box_type === "subscription" ? " / month" : ""}</p>
        <input className={inputCls} placeholder="Tagline (short)" value={f.tagline} onChange={set("tagline")} />
        <input required className={inputCls} placeholder="Image URL" value={f.image} onChange={set("image")} data-testid="bf-image" />
        {f.image && <img src={f.image} alt="" className="w-full h-32 object-cover rounded-xl bg-[#E7E1B1]" />}

        <div className="rounded-xl bg-[#E7E1B1]/50 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wider text-[#0D530E] font-semibold">AI box images</p>
            <button type="button" onClick={genImages} disabled={genning} data-testid="bf-generate-images"
              className="flex items-center gap-1.5 text-sm bg-[#306D29] hover:bg-[#0D530E] text-white px-3 py-1.5 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>
              {genning ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} {genning ? "Generating…" : "Generate 3 images"}
            </button>
          </div>
          <p className="text-[11px] text-[#5C5C5C]">Products · products in a basket · branded AfriGate box. Generated from the items below (~20s). Tap a thumbnail to use it as the cover.</p>
          {f.gallery?.length > 0 && (
            <div className="flex gap-2 overflow-x-auto" data-testid="bf-gallery">
              {f.gallery.map((g, i) => (
                <button type="button" key={i} onClick={() => setF({ ...f, image: g })}
                  className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${f.image === g ? "border-[#306D29]" : "border-transparent"}`}>
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <textarea className={inputCls} rows={2} placeholder="Description" value={f.description} onChange={set("description")} />
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider text-[#5C5C5C] font-semibold">What's inside</p>
            <button type="button" onClick={addItem} data-testid="bf-add-item" className="flex items-center gap-1 text-sm text-[#306D29] font-medium"><Plus size={14} /> Add item</button>
          </div>
          <div className="space-y-2">
            {f.items.map((it, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input className={`flex-1 min-w-0 ${inputCls}`} placeholder="Item name" value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} data-testid={`bf-item-name-${i}`} />
                <input type="number" min="1" className="w-16 shrink-0 bg-white border border-black/10 rounded-xl px-2.5 py-2.5 text-sm text-center focus:ring-2 focus:ring-[#306D29] focus:outline-none" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`bf-item-qty-${i}`} />
                <button type="button" onClick={() => rmItem(i)} className="text-[#B23B3B] shrink-0 w-8 h-8 flex items-center justify-center"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active (visible in shop)</label>
        <button type="submit" disabled={saving} data-testid="bf-submit" className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>{saving ? "Saving…" : editing ? "Save changes" : "Create box"}</button>
      </form>
    </Modal>
  );
}
